/**
 * The birds' pixel art (lifted as-is from the shared prototype engine's
 * `engine/src/bird-art.js`, which lantern-water's flocks draw with): hand-
 * authored frames at the traveler's density (48 × 64 px per 1.18 × 1.59 m,
 * about 40 px a metre), every frame facing right (the shader mirrors it);
 * letters are palette keys so the pigeon's colour morphs share one drawing.
 * `birdAtlas()` paints them into one nearest-filtered canvas texture: a row
 * per morph (`birdRows`), a column per frame (`birdFrames`), `birdCell` px
 * square cells, the feet on row `birdFeetRow`. Prototype art the designer
 * accepted as the look (ADR 0028 D1), drawn in code: no file ships.
 */
import * as THREE from 'three';

export const birdCell = 24;
export const birdFeetRow = 19;
export const birdFrames = ['idle', 'look', 'peckA', 'peckB', 'hop', 'flapUp', 'flapMid', 'flapDown', 'glide'] as const;
export const birdRows = ['pigeon', 'pigeonDark', 'pigeonPale', 'gull'] as const;
/** World metres per sprite pixel (the prototype traveler's 1.59 m over 64 px). */
export const birdPixel = 1.59 / 64;

type BirdFrame = (typeof birdFrames)[number];
type BirdRow = (typeof birdRows)[number];
type Frames = Readonly<Record<BirdFrame, readonly string[]>>;

// o outline, d shade, m mid, b body, w wing, h highlight, r wing bar,
// g/p neck sheen, s breast, t tail band, y beak/cere, e eye, f feet.
const pigeon: Frames = {
  idle: [
    '..........oo...',
    '.........odmo..',
    '.........mmemy.',
    '.........gpmo..',
    '....oooo.gpm...',
    'otthhwwwgpps...',
    'otbrwrwwbpsso..',
    '.obbrwrwbbss...',
    '..odmmmmmsso...',
    '....ooddddo....',
    '......f.f......',
  ],
  look: [
    '...........oo..',
    '..........odmo.',
    '..........mmemy',
    '..........gpmo.',
    '....ooooogppm..',
    'otthhwwwgppso..',
    'otbrwrwwbpsso..',
    '.obbrwrwbbss...',
    '..odmmmmmsso...',
    '....ooddddo....',
    '.....f...f.....',
  ],
  peckA: [
    '...............',
    '...............',
    '....oooo.......',
    '.tthhwwwoo.....',
    'otbrwrwwggoo...',
    'otbbrwrwbpgdo..',
    '.obbwwwbbspmemy',
    '..odmmmmmsomo..',
    '....ooddddo....',
    '......f.f......',
    '...............',
  ],
  peckB: [
    '...............',
    '...............',
    '.oo............',
    'otthoooo.......',
    'otbhhwwwoo.....',
    '.obrwrwwggo....',
    '..obrwrwbpgo...',
    '...odmmmmspdo..',
    '....ooddddodmo.',
    '......f.f..mmey',
    '............o..',
  ],
  hop: [
    '...............',
    '.........oo....',
    '........odmo...',
    '........mmemy..',
    '....oooogpmo...',
    'otthhwwwgpps...',
    'otbrwrwwbpsso..',
    '.obbrwrwbbss...',
    '..odmmmmmsso...',
    '....oodddfo....',
    '...............',
  ],
  flapUp: [
    '...o...........',
    '...owo.........',
    '....owwo.......',
    '....owhwo......',
    '.....owrwo.....',
    '.....owwro.oo..',
    '......owwoodmo.',
    'otthhbbbbgpmemy',
    '.obbmmmmmspso..',
    '..ooddddddso...',
    '....ooooooo....',
  ],
  flapMid: [
    '...............',
    '...............',
    '...............',
    '..........oo...',
    '....ooo..odmo..',
    '...owhwoogpmemy',
    'otthwwwwwbpso..',
    '.obbmwwwwwsso..',
    '..oodowwwoo....',
    '....owwro......',
    '.....oo........',
  ],
  flapDown: [
    '...............',
    '...............',
    '..........oo...',
    '.........odmo..',
    '..ooooooogpmemy',
    'otthhbbbbbpso..',
    '.obbwwwwwwso...',
    '...owwwrwo.....',
    '...owwrwo......',
    '..owwro........',
    '..owo..........',
    '..oo...........',
  ],
  glide: [
    '...............',
    '...............',
    '.oo............',
    '.owwoo.........',
    '..owhwwoo......',
    '...owrwrwoo.oo.',
    'otthhbbwwwgodmo',
    'otbbmmmmmspmemy',
    '.ooddddddsso...',
    '...ooooooo.....',
    '...............',
  ],
};

const gull: Frames = {
  idle: [
    '...........oo....',
    '..........ohho...',
    '..........hhhey..',
    '..........hhhoyr.',
    '.........ohhho...',
    '....ooooohhhh....',
    '..oommmmmmhhho...',
    'okkmmmmwmmhhhso..',
    'okkkmmmmmhhhhs...',
    '.oookddhhhhhso...',
    '.....oosssso.....',
    '.......f..f......',
    '.......f..f......',
  ],
  look: [
    '............oo...',
    '...........ohho..',
    '...........hhhey.',
    '...........hhhoyr',
    '..........ohhho..',
    '....oooooohhhh...',
    '..oommmmmmmhhho..',
    'okkmmmmwmmhhhso..',
    'okkkmmmmmhhhhs...',
    '.oookddhhhhhso...',
    '.....oosssso.....',
    '.......f..f......',
    '.......f..f......',
  ],
  peckA: [
    '.................',
    '.................',
    '.................',
    '....ooooooo......',
    '..oommmmmmmoo....',
    'okkmmmmwmmhhhoo..',
    'okkkmmmmmhhhhhho.',
    '.oookddhhhhhhhhey',
    '.....oossssooohyr',
    '.......f..f......',
    '.......f..f......',
    '.................',
    '.................',
  ],
  peckB: [
    '.................',
    '.................',
    '.................',
    '..oooooo.........',
    'okkmmmmmmoo......',
    'okkmmmmwmmhoo....',
    '.oomkmmmmhhhhoo..',
    '...ooddhhhhhhhho.',
    '.....oossssoohhey',
    '.......f..f...oyr',
    '.......f..f......',
    '.................',
    '.................',
  ],
  hop: [
    '.................',
    '...........oo....',
    '..........ohho...',
    '..........hhhey..',
    '..........hhhoyr.',
    '....oooooohhho...',
    '..oommmmmmhhhh...',
    'okkmmmmwmmhhhso..',
    'okkkmmmmmhhhhs...',
    '.oookddhhhhhso...',
    '.....oosssfo.....',
    '.................',
    '.................',
  ],
  flapUp: [
    '..ok.............',
    '..okko...........',
    '...okwmo.........',
    '....ommmo........',
    '.....ommmo.......',
    '......ommmo......',
    '.......ommo...oo.',
    '........omo..ohho',
    '..ooooooohhhhhhey',
    'otthhhhhhhhhhhoyr',
    '.oohhhhhhssso....',
    '....oosssso......',
    '.................',
  ],
  flapMid: [
    '.................',
    '.................',
    '.................',
    '.................',
    '.................',
    '..............oo.',
    '..ooooooooo..ohho',
    'okkkmmmmmmmoohhey',
    'okmmmmwwwmmmhhoyr',
    '.oohkkmmmmmshho..',
    '....oosssssso....',
    '.................',
    '.................',
  ],
  flapDown: [
    '.................',
    '.................',
    '.................',
    '.................',
    '..............oo.',
    '.............ohho',
    '..ooooooooooohhey',
    'otthhhhhhhhhhhoyr',
    '.oohhhmmmmmsso...',
    '....ommmmmo......',
    '...ommmmo........',
    '..okkmo..........',
    '..okko...........',
    '..oo.............',
  ],
  glide: [
    '.................',
    '.................',
    '.................',
    '.okk.............',
    '.okkoo...........',
    '..okmmmoo........',
    '...ommmmmmoo..oo.',
    '....ooommmmmooho.',
    'otthhhhhhhhhhhhey',
    '.oohhhhhhhsssoyr.',
    '....oooooooo.....',
    '.................',
    '.................',
  ],
};

// Palettes read as clustered tones of one hue rather than flat fills.
const palettes: Readonly<Record<BirdRow, Readonly<Record<string, string>>>> = {
  pigeon: {
    o: '#262636', d: '#4b5068', m: '#6c7690', b: '#8b97ae', w: '#aab3c4',
    h: '#c7ced9', r: '#2d3143', g: '#4f9a7c', p: '#8c64a3', s: '#9d95b1',
    t: '#34384a', y: '#e8dfc9', e: '#f08c3c', f: '#cf6b5b',
  },
  pigeonDark: {
    o: '#221f2b', d: '#3b3a4c', m: '#555569', b: '#6b6c80', w: '#7f7f92',
    h: '#9b9aab', r: '#29263a', g: '#3f8a6d', p: '#7d568f', s: '#7c7389',
    t: '#2b2a37', y: '#e2d8c0', e: '#ef8a3b', f: '#c9665a',
  },
  pigeonPale: {
    o: '#3a3230', d: '#8a7a73', m: '#b3a79d', b: '#d3cabf', w: '#e3ddd3',
    h: '#f1ede6', r: '#8b5d48', g: '#6f9f86', p: '#a07aa9', s: '#cdb9b5',
    t: '#6f5a50', y: '#efe6d1', e: '#e98a3c', f: '#d27a68',
  },
  gull: {
    o: '#2b3140', d: '#8391a3', m: '#a3b1c2', w: '#eef2f6', k: '#1d2029',
    h: '#f2f4f7', s: '#d3dbe4', t: '#c2ccd8', y: '#f2c64a', r: '#d8513d',
    e: '#20222a', f: '#e5a86a', b: '#b8c4d2',
  },
};

/** The atlas, painted once per call (the scene view owns and disposes it). */
export const birdAtlas = (): THREE.CanvasTexture => {
  const canvas = document.createElement('canvas');
  canvas.width = birdCell * birdFrames.length;
  canvas.height = birdCell * birdRows.length;
  const g = canvas.getContext('2d');
  if (g !== null) {
    birdRows.forEach((row, ri) => {
      const maps = row === 'gull' ? gull : pigeon;
      const palette = palettes[row];
      birdFrames.forEach((frame, fi) => {
        const map = maps[frame];
        // Feet (or the lowest body row in flight) sit on the feet row; the body is centred so mirroring keeps the bird over the same spot.
        const width = Math.max(...map.map((r) => r.length));
        const ox = fi * birdCell + Math.floor((birdCell - width) / 2);
        const oy = ri * birdCell + birdFeetRow + 1 - map.length;
        map.forEach((line, y) => {
          for (let x = 0; x < line.length; x += 1) {
            const ch = line.charAt(x);
            if (ch === '.') continue;
            g.fillStyle = palette[ch] ?? '#ff00ff';
            g.fillRect(ox + x, oy + y, 1, 1);
          }
        });
      });
    });
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  return texture;
};
